import { roundCoordinate, type DiagramElement, type DiagramPitch, type DiagramTeam, type FormationPosition } from '@ukinfo/shared';

/**
 * Rechnet eine Formation in Spieler einer Skizze um - reine Funktion, ohne React.
 *
 * Eine Formation steht auf einem ganzen Feld: eigenes Tor unten, Spielrichtung nach oben. Die Skizze
 * kennt drei Felder:
 * - Ganzes Feld: 1:1; mit `half` nur auf einer Haelfte (die eigene, unten - gespiegelt die obere).
 * - Halbes Feld: das Tor liegt oben, gespielt wird darauf zu. Die Formation wird auf die Haelfte
 *   umgerechnet (die Prozentwerte gelten fuer das halbe Feld). Gespiegelt steht die Gegenseite:
 *   Torwart am Tor, der Angriff an der Mittellinie.
 * - Freie Flaeche: proportional uebernommen.
 *
 * `mirrored` ist die Gegnerseite: Drehung um 180 Grad (links bleibt aus Sicht der Spieler links).
 *
 * Konvention der Skizzen (wie im Standardkatalog fuer Torhueter): Torwart = Team N mit Beschriftung "TW".
 * Alle anderen Spieler gehoeren zum gewaehlten Team und tragen ihr Kuerzel als Beschriftung.
 */

export interface FormationInsertOptions {
  /** Mannschaft der Feldspieler. */
  team: Extract<DiagramTeam, 'A' | 'B'>;
  /** Gegnerseite: um 180 Grad gedreht (Torwart oben, Spielrichtung nach unten). */
  mirrored: boolean;
  /** Feld der Skizze, in die eingefuegt wird. */
  pitch: DiagramPitch;
  /** Nur eine Haelfte des ganzen Feldes (bei halbem Feld und freier Flaeche ohne Wirkung bzw. immer). */
  half?: boolean;
}

/** Beschriftung des Torwarts in Skizze und Formation. */
export const KEEPER_LABEL = 'TW';

export function isKeeper(position: Pick<FormationPosition, 'label' | 'role'>): boolean {
  return position.role === 'GK' || (position.role === undefined && position.label.trim().toUpperCase() === KEEPER_LABEL);
}

/** Auf halbe Einheiten gerundet und im Feld gehalten (die Haelfte eines ungeraden y-Werts ergibt eine halbe Einheit). */
const clampCoordinate = (value: number): number => Math.min(100, Math.max(0, roundCoordinate(value)));

/** Position der Formation (Prozent des ganzen Feldes) auf dem Zielfeld, ohne Spiegelung. */
function place(position: Pick<FormationPosition, 'x' | 'y'>, pitch: DiagramPitch, half: boolean): { x: number; y: number } {
  // Ganzes Feld nur auf der eigenen (unteren) Haelfte: y von 0..100 auf 50..100 stauchen.
  if (pitch === 'FULL' && half) return { x: clampCoordinate(position.x), y: clampCoordinate(50 + position.y / 2) };
  // Halbes Feld und freie Flaeche rechnen in Prozent des eigenen Feldes: dieselben Werte, kleinere Hoehe.
  return { x: clampCoordinate(position.x), y: clampCoordinate(position.y) };
}

export function formationToElements(formation: { positions: readonly FormationPosition[] }, options: FormationInsertOptions): DiagramElement[] {
  return formation.positions.map((position): DiagramElement => {
    const placed = place(position, options.pitch, options.half === true);
    const x = options.mirrored ? 100 - placed.x : placed.x;
    const y = options.mirrored ? 100 - placed.y : placed.y;
    return {
      type: 'player',
      team: isKeeper(position) ? 'N' : options.team,
      x: clampCoordinate(x),
      y: clampCoordinate(y),
      label: position.label,
    };
  });
}
